import React, { useState, FC } from 'react';
import { makeStyles } from 'design-system/styles';
import { Button, Spinner, TextInput } from 'design-system';
import { DialogActions } from 'react-scale/components/design-system/Dialog';
import { invokeCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import { handleApiFetchError } from '@scale/frontend-lib/api-fetch';
import { STAGE, ALERT_CONTENT } from '../../constants';

interface CreateTeamViewProps {
  team: { id: string; name: string };
  setTeam: React.Dispatch<React.SetStateAction<{ id: string; name: string }>>;
  setCurrentStage: React.Dispatch<React.SetStateAction<STAGE>>;
  setAlertStatus: React.Dispatch<React.SetStateAction<{ severity: string; content: any } | null>>;
  TeamSelector: () => JSX.Element;
}

const CreateTeamView: FC<CreateTeamViewProps> = ({
  team: parentTeam,
  setTeam,
  setCurrentStage,
  setAlertStatus,
  TeamSelector,
}) => {
  const classes = useStyle();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [subteamName, setSubteamName] = useState<string>('Untitled');
  const handleCreateSubteam = async () => {
    setIsFetching(true);
    try {
      const job = await invokeCorpJob('createSubteam', {
        parentTeamId: parentTeam.id,
        subteamName,
      });
      setIsFetching(false);
      const results: any | undefined = job.jsonOutput;

      if (job.status === 'completed' && results.subteamId) {
        setTeam({ id: results.subteamId, name: subteamName });
        setCurrentStage(STAGE.ASSIGN_TEAM_VIEW);
        setAlertStatus(ALERT_CONTENT.addTeamSuccess);
      } else if (results) {
        setAlertStatus({ severity: 'error', content: results.message });
      } else {
        setAlertStatus(ALERT_CONTENT.error);
      }
    } catch (error) {
      setIsFetching(false);
      setAlertStatus({ severity: 'error', content: error.response.error });
      handleApiFetchError(error, 'Failed to create a worker team.');
    }
    setTimeout(() => {
      setAlertStatus(null);
    }, 1000 * 10);
  };

  return (
    <div className={classes.container}>
      <fieldset className="fieldset fieldset--long">
        <label>Choose Parent Team</label>
        <TeamSelector />
      </fieldset>

      <fieldset className="fieldset fieldset--long">
        <label>Subteam Name</label>
        <TextInput
          value={subteamName}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
            setSubteamName(event.target.value);
          }}
          error={!subteamName}
        />
      </fieldset>
      <DialogActions className={classes.actionWraper}>
        <Button type="primary" disabled={isFetching || !subteamName} onClick={handleCreateSubteam}>
          {isFetching && <Spinner size="sm" />}
          Create
        </Button>
      </DialogActions>
    </div>
  );
};

const useStyle = makeStyles({
  container: {
    width: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& label': {
      padding: '10px 0',
      fontSize: 16,
      fontWeight: 'bold',
    },
    '& input': {
      width: '80% !important',
    },
  },
  actionWraper: {
    width: '100%',
  },
});

export default CreateTeamView;
